import { cloneElement, FC, useEffect, useState } from 'react';
import { Box, Divider, StackDivider, VStack } from '@chakra-ui/react';
import { Pagination } from './Pagination';
import { TrackingLabelProps } from '../../types/global';
import { Loader } from '../loader';

export interface PaginationListProps extends TrackingLabelProps {
  children: any;
  isLoading?: boolean;
  listItems: any[];
  listKey?: string;
  pageSize?: number;
}

/**
 * A container for a paginated list of children
 * @param props
 * @property  children - The element to render in each list item.
 * @property  listItems - An array containing the properties to bind to each items in the list.
 * @property  listKey -  Name of key field to uniquely identify each list element.
 * @property  pageSize - defaults to 5
 */
export const PaginationList: FC<PaginationListProps> = ({
  children,
  isLoading,
  listItems,
  listKey,
  pageSize = 5,
  trackingLabel,
}) => {
  const [page, setPage] = useState(1);
  const totalCount = listItems?.length ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  useEffect(() => {
    // If data has been filtered, reset to page 1
    setPage(1);
  }, [listItems]);

  if (isLoading) return <Loader />;

  return (
    <>
      <VStack
        align="stretch"
        divider={<StackDivider color="brand.primary.lightGrey" />}
        spacing={5}
        minHeight="425px"
      >
        {listItems?.slice(startIndex, endIndex).map((item, index) =>
          cloneElement(children, {
            ...item,
            key: listKey ? `${item[listKey]}-${index}` : index,
          })
        )}
      </VStack>
      <Divider color="brand.primary.lightGrey" />
      <Box mt="5">
        {totalPages > 1 && (
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            trackingLabel={trackingLabel}
          />
        )}
      </Box>
    </>
  );
};
